// @flow
import * as React from 'react';
import {
    defineMessages, injectIntl, IntlShape,
} from 'react-intl';
import { withRouter } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { get } from 'lodash';
import ApiClient from 'speed-js-core/src/api/ApiClient';
import type { Confirmation } from 'speed-js-react';
import { withConfirmation, connectAPI } from 'speed-js-react';
import { HTTP_400_BAD_REQUEST } from 'speed-js-core';
import DeleteIcon from 'esm-web-pro/components/layout/sidePanel/icons/DeleteIcon';
import EditIcon from 'esm-web-pro/components/layout/sidePanel/icons/EditIcon';
import TimelineIcon from 'esm-web-pro/components/layout/sidePanel/icons/TimelineIcon';
import MainActions from 'esm-web-pro/components/layout/sidePanel/MainActions';
import PlusIcon from 'esm-web-pro/components/layout/sidePanel/icons/Plus';
import OrderIcon from 'esm-web-pro/components/layout/sidePanel/icons/Order';
import actionMessages from 'esm-web-pro/messages/actionMessages';
import Point11AddContent from 'esm-web-pro/containers/pages/network/objects/point11/Point11AddContent';
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';
import * as sidePanelActions from 'esm-web-pro/redux/side-panel/actions';
import SidePane from 'esm-web-pro/components/layout/SidePane';
import TransformPoint from 'esm-web-pro/containers/pages/network/objects/gispoint/TransformPoint';
import InfrastructureDocumentPermitForm
    from 'esm-web-pro/containers/pages/network/forms/InfrastructureDocumentPermitForm';
import documentsMessages from 'esm-web-pro/messages/documentsMessages';
import type { PermitDocument } from 'esm-web-pro/types/networks';
import PermitIcon from 'esm-web-pro/components/layout/sidePanel/icons/PermitIcon';
import sidePanelMessages from 'esm-web-pro/messages/sidePanelMessages';
import Point11Reorder from './reorder/Point11Reorder';
import Point11Editor from './Point11Editor';


type SidePanelActionProps = {
    openPanel: (key: string, url: ?string, sensitivity?: boolean) => { key: string, url: string },
    closePanel: (key: string, skipSensitivity?: boolean) => { key: string },
    setSensitivity: (key: string, sensitivity: boolean) => void,
}

type Props = {
    ...$Exact<SidePanelActionProps>,
    point: Object,
    onEdit: Function,
    onAddContent: Function,
    confirm: any | (confirmation: Confirmation) => void,
    intl: any | IntlShape,
    history: Object,
    match: Object,
    map: Object,
    deleteObject?: Object,
    resource: string,
    // eslint-disable-next-line react/no-unused-prop-types
    editor: BaseEditor,
    updateObject: Function,
    onView: Function,
    objectLayer: Object,
}

const msg = defineMessages({
    deleteTitle: {
        id: 'delete-point-title',
        defaultMessage: 'Are you sure you want to delete the point? All related points from layer 1.2 will be '
            + 'deleted also.',
    },
    cancelDelete: {
        id: 'delete-point-cancel',
        defaultMessage: 'Cancel',
    },
    confirmDelete: {
        id: 'delete-point-confirm',
        defaultMessage: 'Confirm',
    },
    cantDelete: {
        id: 'cant-delete-point11',
        defaultMessage: 'The point is container point for the line/s. You should delete the line/s first.',
    },
    title: {
        id: 'point-editor-title',
        defaultMessage: 'Edit point',
    },
});


const POINT_EDIT_KEY = 'POINT1_1_EDIT';
const ADD_CONTENT_KEY = 'ADD_CONTENT';
const ADD_ASSET_KEY = 'ADD_ASSET';
const POINT_REORDER_KEY = 'POINT1_1_REORDER';
const PERMIT_DOCUMENT_KEY = 'POINT1_1_PERMIT_DOCUMENT';

const mapDispatchToProps = (dispatch) => (
    bindActionCreators(sidePanelActions, dispatch)
);

@connect(null, mapDispatchToProps)
@connectAPI((apiClient: ApiClient) => ({
    deleteObject: {
        send: (resource: string, mapUuid: string, id: string) => get(apiClient.resources,
            resource).delete(mapUuid, id),
    },
}))
@withRouter
@withConfirmation
@injectIntl
class Point11Actions extends React.Component <Props> {
    static defaultProps = {
        deleteObject: undefined,
        confirm: undefined,
        intl: undefined,
    };

    handleClose = (skipSensitivity = false) => {
        const { closePanel } = this.props;
        closePanel(POINT_EDIT_KEY, skipSensitivity);
    };

    handleContentClose = (skipSensitivity = false) => {
        const { closePanel } = this.props;
        closePanel(ADD_CONTENT_KEY, skipSensitivity);
    };

    handleAssetClose = (skipSensitivity = false) => {
        const { closePanel } = this.props;
        closePanel(ADD_ASSET_KEY, skipSensitivity);
    };

    handleReorderClose = (skipSensitivity = true) => {
        const { closePanel } = this.props;
        closePanel(POINT_REORDER_KEY, skipSensitivity);
    };

    handlePermitDocumentClose = (skipSensitivity = true) => {
        const { closePanel } = this.props;
        closePanel(PERMIT_DOCUMENT_KEY, skipSensitivity);
    };

    handleSave = (newValues: Object) => {
        const { onEdit } = this.props;
        onEdit(newValues);
        this.handleClose(true);
    };

    handleAddContentSave = (newValues: Object) => {
        const { onAddContent } = this.props;
        onAddContent(newValues);
        this.handleClose(true);
    };

    handlePermitDocumentSave = (document: PermitDocument) => {
        const { onAddContent, point } = this.props;
        onAddContent({ permit_documents: [...(point.permit_documents || []), document] });
        this.handlePermitDocumentClose(true);
    };

    getActions = () => {
        const {
            map: { is_draft: isDraft }, intl: { formatMessage }, history, match, editor, resource,
            openPanel, point, objectLayer,
        } = this.props;

        const actions = [];
        if (isDraft && point.type !== 'fork') {
            actions.push({
                key: 'point11-plus',
                icon: <PlusIcon />,
                marginVertical: '15px',
                disabled: editor.state.loading,
                callback: () => openPanel(
                    ADD_CONTENT_KEY,
                    formatMessage(sidePanelMessages.addContent),
                    match.url,
                    true,
                ),
                tooltip: formatMessage(actionMessages.addContent),
                radius: '0%',
                color: 'transparent',
            });
        }

        let editTooltip;
        if (!objectLayer && !editor.state.loading) {
            editTooltip = formatMessage(actionMessages.objectNotOnTheMap);
        } else {
            editTooltip = formatMessage(actionMessages.edit);
        }

        if (isDraft) {
            actions.push({
                key: 'point11-edit',
                icon: <EditIcon size="small" />,
                marginVertical: '15px',
                disabled: editor.state.loading || !objectLayer,
                callback: () => openPanel(POINT_EDIT_KEY, '', match.url, true),
                tooltip: editTooltip,
                radius: '0%',
                color: 'transparent',
            });
            actions.push({
                key: 'point11-permit-document',
                icon: <PermitIcon size="small" />,
                marginVertical: '15px',
                disabled: editor.state.loading || !objectLayer,
                callback: () => openPanel(
                    PERMIT_DOCUMENT_KEY,
                    formatMessage(sidePanelMessages.permitDocument),
                    match.url,
                    true,
                ),
                tooltip: formatMessage(documentsMessages.addNewPermit),
                radius: '0%',
                color: 'transparent',
            });
        }

        if (isDraft && point.type !== 'pop' && point.type !== 'fork') {
            actions.push({
                key: 'point11-order',
                icon: <OrderIcon />,
                marginVertical: '15px',
                disabled: editor.state.loading,
                callback: () => openPanel(
                    POINT_REORDER_KEY,
                    formatMessage(sidePanelMessages.reorderContent),
                    match.url,
                    true,
                ),
                tooltip: formatMessage(actionMessages.reorderContent),
                radius: '0%',
                color: 'transparent',
            });
        }

        actions.push({
            key: 'point11-timeline',
            icon: <TimelineIcon />,
            marginVertical: '15px',
            callback: () => {
                history.push(`${match.url}/timeline/${resource}/${point.identity}`.replace('//', '/'));
            },
            tooltip: formatMessage(actionMessages.timeline),
            radius: '0%',
            color: 'transparent',
        });

        if (isDraft) {
            actions.push({
                key: 'point11-delete',
                icon: <DeleteIcon size="small" />,
                marginVertical: '15px',
                disabled: editor.state.loading,
                callback: this.deletePoint,
                tooltip: formatMessage(actionMessages.delete),
                radius: '0%',
                color: 'transparent',
            });
        }

        return actions;
    };


    handleDeleteResponse = (response: Object) => {
        const {
            confirm, history, match, intl: { formatMessage },
        } = this.props;

        if (response.status === HTTP_400_BAD_REQUEST) {
            confirm({
                message: response.data.error,
                actions: {
                    cancel: {
                        label: formatMessage(msg.cancelDelete),
                        callback: () => {
                        },
                        type: 'negative',
                    },
                },
            });
        } else {
            history.push(match.url.split('/layer')[0]);
        }
    };

    deletePoint = () => {
        const {
            point, deleteObject, intl: { formatMessage }, confirm, resource, map: { uuid: mapUuid },
        } = this.props;

        if (point.line_starts.length > 0 || point.line_ends.length > 0) {
            confirm({
                message: formatMessage(msg.cantDelete),
                actions: {
                    cancel: {
                        label: formatMessage(msg.cancelDelete),
                        callback: () => {
                        },
                        type: 'negative',
                    },
                },
            });
        } else {
            confirm({
                message: formatMessage(msg.deleteTitle),
                actions: {
                    cancel: {
                        label: formatMessage(msg.cancelDelete),
                        callback: () => {
                        },
                        type: 'negative',
                    },
                    confirm: {
                        label: formatMessage(msg.confirmDelete),
                        callback: () => {
                            if (deleteObject) {
                                deleteObject(`${resource}.geo`, mapUuid, point.identity).then((response) => {
                                    this.handleDeleteResponse(response);
                                });
                            }
                        },
                        type: 'positive',
                    },
                },
            });
        }
    };

    handleError = ({ data }: Object) => {
        const { confirm, intl: { formatMessage } } = this.props;

        if (data) {
            confirm({
                message: data[0],
                actions: {
                    cancel: {
                        label: formatMessage(msg.cancelDelete),
                        callback: () => {
                        },
                        type: 'negative',
                    },
                },
            });
        }
    };

    render() {
        const {
            point, map, resource, history, match, updateObject, editor, intl,
            intl: { formatMessage }, onView, objectLayer,
        } = this.props;

        let title = point.ref;
        if (point.type === 'pop' && point.alias) {
            title = point.alias;
        }

        return (
            <>
                <SidePane
                    id={POINT_EDIT_KEY}
                    key={point.identity}
                    titleLabel={formatMessage(msg.title)}
                    title={title}
                    onClose={this.handleClose}
                >
                    <Point11Editor
                        id={POINT_EDIT_KEY}
                        objectLayer={objectLayer}
                        point={point}
                        onView={onView}
                        onClose={this.handleClose}
                        onSave={this.handleSave}
                        mapIdentity={map.identity}
                        resource={resource}
                        match={match}
                        history={history}
                        updateObject={updateObject}
                        editor={editor}
                        onError={this.handleError}
                    />
                </SidePane>
                <SidePane
                    id={ADD_CONTENT_KEY}
                    key={point.uuid}
                    title={formatMessage(sidePanelMessages.addContent)}
                    sensitive
                >
                    {point.type === 'linecut' ? (
                        <TransformPoint
                            onClose={this.handleContentClose}
                            onSave={() => this.handleContentClose(true)}
                            point={point}
                            pointId={point.gis_point}
                            map={map}
                            loadFeatures={editor._loadFeatures}
                        />
                    ) : (
                        <Point11AddContent
                            onClose={this.handleContentClose}
                            updateObject={updateObject}
                            onSave={this.handleAddContentSave}
                            point={point}
                            map={map}
                            editor={editor}
                            id={ADD_CONTENT_KEY}
                        />
                    )}
                </SidePane>
                <SidePane
                    id={POINT_REORDER_KEY}
                    key={`REORDER-${point.uuid}`}
                    title={formatMessage(sidePanelMessages.reorderContent)}
                >
                    <Point11Reorder
                        onClose={this.handleReorderClose}
                        updateObject={updateObject}
                        onSave={() => this.handleContentClose(true)}
                        point={point}
                        map={map}
                        intl={intl}
                    />
                </SidePane>
                <SidePane
                    id={PERMIT_DOCUMENT_KEY}
                    key="permit-document"
                    title={formatMessage(sidePanelMessages.permitDocument)}
                >
                    <InfrastructureDocumentPermitForm
                        onClose={this.handlePermitDocumentClose}
                        onSave={this.handlePermitDocumentSave}
                        id={point.uuid}
                        model={point.type}
                    />
                </SidePane>

                <MainActions main actions={this.getActions()} />
            </>
        );
    }
}

export default Point11Actions;
